import React, { useState } from 'react';
import { LAYER_TEMPLATES } from '../../utils/layerUtils';
import { LayerType } from '../../types/modelBuilder';
import '../../styles/modelBuilder.css';

export const LayerPalette: React.FC = () => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>('convolutional');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = {
    convolutional: {
      label: '🖼️ Convolutional',
      types: ['conv2d', 'conv1d', 'conv3d', 'depthwise_separable_conv2d', 'conv_transpose2d'] as LayerType[],
    },
    normalization: {
      label: '⚖️ Normalization',
      types: ['batch_norm2d', 'batch_norm', 'layer_norm', 'group_norm', 'instance_norm2d'] as LayerType[],
    },
    pooling: {
      label: '⬇️ Pooling',
      types: ['adaptive_avg_pool2d', 'adaptive_max_pool2d', 'global_avg_pool', 'global_max_pool'] as LayerType[],
    },
    linear: {
      label: '🔲 Linear',
      types: ['linear'] as LayerType[],
    },
    sequence: {
      label: '🔄 Sequence',
      types: ['lstm', 'gru', 'rnn'] as LayerType[],
    },
    attention: {
      label: '👁️ Attention',
      types: ['multihead_attention'] as LayerType[],
    },
    operation: {
      label: '⚙️ Operations',
      types: ['add', 'concatenate', 'multiply', 'upsample'] as LayerType[],
    },
    other: {
      label: '📦 Other',
      types: ['dropout', 'embedding'] as LayerType[],
    },
  };

  const handleDragStart = (e: React.DragEvent, layerType: LayerType) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('layerType', layerType);
  };

  const filteredCategories = Object.entries(categories).reduce((acc, [key, cat]) => {
    const filtered = cat.types.filter(type => {
      const template = LAYER_TEMPLATES[type];
      const term = searchTerm.toLowerCase();
      return (
        template.name.toLowerCase().includes(term) ||
        template.description.toLowerCase().includes(term)
      );
    });

    if (filtered.length > 0) {
      (acc as Record<string, typeof cat>)[key] = { ...cat, types: filtered };
    }
    return acc;
  }, {} as Record<string, typeof categories[keyof typeof categories]>);

  return (
    <div className="layer-palette">
      <div className="palette-header">
        <h3>Layer Palette</h3>
        <input
          type="text"
          placeholder="Search layers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="palette-search"
        />
      </div>

      <div className="palette-content">
        {Object.entries(filteredCategories).length === 0 ? (
          <div className="no-results">No layers match your search</div>
        ) : (
          Object.entries(filteredCategories).map(([categoryKey, category]) => (
            <div key={categoryKey} className="palette-category">
              <button
                className="category-header"
                onClick={() =>
                  setExpandedCategory(
                    expandedCategory === categoryKey ? null : categoryKey
                  )
                }
              >
                <span className="category-label">{category.label}</span>
                <span className={`expand-icon ${expandedCategory === categoryKey ? 'open' : ''}`}>
                  ▶
                </span>
              </button>

              {expandedCategory === categoryKey && (
                <div className="category-items">
                  {category.types.map((layerType) => {
                    const template = LAYER_TEMPLATES[layerType];
                    return (
                      <div
                        key={layerType}
                        className="palette-item"
                        draggable
                        onDragStart={(e) => handleDragStart(e, layerType)}
                        title={template.description}
                      >
                        <div className="item-icon">{template.icon}</div>
                        <div className="item-info">
                          <div className="item-name">{template.name}</div>
                          <div className="item-description">{template.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="palette-footer">
        <p>💡 Drag layers to the canvas to add them</p>
      </div>
    </div>
  );
};

export default LayerPalette;
